import React, { useState, useRef } from 'react';
import { Typography, Box, Button, Modal, Checkbox, ListItemText, Divider, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { useTheme as useMUITheme } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const SKUBuilderModal = (props) => {
  const { theme } = useTheme();
  const muiTheme = useMUITheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up('md'));
  
  // For touch-based drag and drop
  const [activeItem, setActiveItem] = useState(null);
  const [touchStartY, setTouchStartY] = useState(0);
  const itemRefs = useRef({});

  const styles = {
    modal: {
      position: 'absolute',
      top: '53%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxHeight: '80vh',
      width: isSmallScreen ? '90%' : (isMediumScreen ? '85%' : (isLargeScreen ? '80%' : 'auto')),
      overflow: 'auto',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: isSmallScreen ? 1 : (isMediumScreen ? 2 : 4),
      outline: 'none',
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`
    },
    sectionTitle: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      marginBottom: 1
    },
    checkbox: {
      color: theme.color.primary,
      '&.Mui-checked': {
        color: theme.color.primary,
      }
    },
    checkboxLabel: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
    },
    listItems: {
      display: 'flex',
      flexDirection: isSmallScreen ? 'column' : (isMediumScreen ? 'column' : 'row'),
      overflowX: 'auto',
      gap: 1,
      padding: 1
    },
    listItem: {
      display: 'flex',
      flexDirection: isSmallScreen ? 'row' : (isMediumScreen ? 'row' : 'column'),
      backgroundColor: theme.background.secondary,
      marginBottom: 1,
      borderRadius: 1,
      border: `1px solid ${theme.border.light}`,
      alignItems: 'center',
      padding: '4px 16px',
      position: 'relative', // For absolute positioning of children
    },
    dragHandle: {
      cursor: 'grab',
      position: 'absolute',
      left: '4px',
      color: theme.color.primary,
      '&:active': {
        cursor: 'grabbing',
      },
    },
    activeItem: {
      opacity: 0.7,
      boxShadow: '0px 0px 10px rgba(0,0,0,0.3)',
    },
    moveIcon: {
      color: theme.color.primary
    },
    previewContainer: {
      backgroundColor: theme.background.secondary,
      padding: 2,
      borderRadius: 1,
      mb: 2,
      border: `1px solid ${theme.border.light}`
    },
    previewTitle: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontWeight: 'bold',
      marginBottom: 1
    },
    previewSku: {
      fontFamily: 'monospace',
      color: theme.color.primary,
      fontSize: '1rem',
      padding: 1,
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: 1,
      overflowX: 'auto',
      whiteSpace: 'nowrap'
    },
    skuPart: {
      fontFamily: theme.fontFamily,
      display: 'inline-block',
      padding: '2px 4px',
      borderRadius: 2,
      backgroundColor: theme.color.primary,
      color: theme.text.negative,
    },
    skuSeparator: {
      display: 'inline-block',
      color: theme.text.secondary,
      margin: '0 2px'
    },
    seperatorSectionContainer: {
      backgroundColor: theme.background.secondary,
      padding: 2,
      borderRadius: 1,
      mb: 2,
    },
    sectionContainer: {
      backgroundColor: theme.background.secondary,
      padding: 2,
      borderRadius: 1
    },
  };

  // Handle input selection change
  const handleInputSelect = (inputName) => {
    props.setInputs(prevInputs =>
      prevInputs.map(input =>
        input.name === inputName ? { ...input, selected: !input.selected } : input
      )
    );
  };

  // Generate SKU preview
  const generateSkuPreview = () => {
    const filteredInputs = props.inputs.filter(input => input.selected);

    if (filteredInputs.length === 0) {
      return <Typography sx={{ color: theme.text.secondary, fontStyle: 'italic' }}>No fields selected</Typography>;
    }

    return (
      <Box sx={styles.previewSku}>
        {filteredInputs.map((input, index) => (
          <React.Fragment key={input.name}>
            <Box component="span" sx={styles.skuPart} title={input.name} >
              {input.name.toUpperCase()}
            </Box>
            {(index < filteredInputs.length - 1) && props.seperator && props.seperator !== "NONE" && (
              <Box component="span" sx={styles.skuSeparator}>{props.seperator}</Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // Handle save button click
  const handleSave = () => {
    props.confirm();
    props.setOpen(false);
  };

  // iOS compatible touch handlers
  const handleTouchStart = (index, e) => {
    // Store the item being dragged and its starting position
    setActiveItem(index);
    setTouchStartY(e.touches[0].clientY);
    
    // Prevent default to avoid scrolling
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (activeItem === null) return;
    
    // Get all items
    const items = props.inputs.map((_, i) => itemRefs.current[`item-${i}`]);
    const activeElement = itemRefs.current[`item-${activeItem}`];
    
    if (!activeElement) return;
    
    // Make the active item follow the finger
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - touchStartY;
    
    // Add visual indication that item is being moved
    activeElement.style.transform = `translateY(${deltaY}px)`;
    activeElement.style.zIndex = 1000;
    
    // Find the item we're hovering over
    for (let i = 0; i < items.length; i++) {
      if (i === activeItem) continue;
      
      const item = items[i];
      if (!item) continue;
      
      const rect = item.getBoundingClientRect();
      
      // If the touch position is within the bounds of this item
      if (touchY >= rect.top && touchY <= rect.bottom) {
        // Reorder the items in state
        props.setInputs(prevInputs => {
          const newInputs = [...prevInputs];
          const [removed] = newInputs.splice(activeItem, 1);
          newInputs.splice(i, 0, removed);
          
          // Update the active item index since the array has changed
          setActiveItem(i);
          return newInputs;
        });
        
        break;
      }
    }
    
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (activeItem === null) return;
    
    // Reset the active item's appearance
    const activeElement = itemRefs.current[`item-${activeItem}`];
    if (activeElement) {
      activeElement.style.transform = '';
      activeElement.style.zIndex = '';
    }
    
    // Clear the active item state
    setActiveItem(null);
  };

  // HTML5 drag and drop handlers (for desktop)
  const onDragStart = (index, e) => {
    e.dataTransfer.setData('text/plain', index);
    // Add delay to enable drag image
    setTimeout(() => {
      setActiveItem(index);
    }, 0);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (index, e) => {
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (draggedIndex === index) return;
    
    props.setInputs(prevInputs => {
      const items = [...prevInputs];
      const [draggedItem] = items.splice(draggedIndex, 1);
      items.splice(index, 0, draggedItem);
      return items;
    });
    
    setActiveItem(null);
  };

  const onDragEnd = () => {
    setActiveItem(null);
  };

  return (
    <Modal open={props.open} onClose={() => props.setOpen(false)}>
      <Box sx={styles.modal} id="skubuilder">
        <Typography variant="h6" component="h2" style={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.primary, marginBottom: 2 }}>
          Build your own SKU
        </Typography>

        {/* SKU Preview Section */}
        <Box sx={styles.previewContainer}>
          <Typography variant="subtitle1" sx={styles.previewTitle}>
            SKU Preview:
          </Typography>
          {generateSkuPreview()}
        </Box>

        <Box sx={styles.seperatorSectionContainer}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            Select your SKU seperator
          </Typography>

          <Box sx={{ display: 'flex', overflowX: 'auto', gap: 2, padding: 1, justifyContent: "space-around" }}>
            {["_", "-", ".", "NONE"].map((seperator, index) => (
              <Paper
                key={index}
                sx={{ ...styles.listItem, bgcolor: seperator === props.seperator ? theme.background.primary : theme.background.secondary, cursor: "pointer" }}
                onClick={() => props.setSeperator(seperator)}
              >
                <ListItemText
                  primary={seperator}
                  sx={{
                    '& .MuiListItemText-primary': styles.checkboxLabel,
                    textAlign: 'center',
                    opacity: seperator === props.seperator ? 1 : 0.5
                  }}
                />
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Combined section for selection and ordering */}
        <Box sx={styles.sectionContainer}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            Select and arrange fields (touch & drag):
          </Typography>
          <Box sx={styles.listItems}>
            {props.inputs.map((input, index) => (
              <Paper
                key={input.name}
                ref={el => (itemRefs.current[`item-${index}`] = el)}
                sx={{
                  ...styles.listItem,
                  ...(activeItem === index ? styles.activeItem : {}),
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                draggable="true"
                onDragStart={(e) => onDragStart(index, e)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(index, e)}
                onDragEnd={onDragEnd}
                onTouchStart={(e) => handleTouchStart(index, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <DragIndicatorIcon sx={styles.dragHandle} />
                <Checkbox
                  checked={input.selected}
                  onChange={() => handleInputSelect(input.name)}
                  sx={{ ...styles.checkbox, ml: 2 }} // Add margin to make room for the drag handle
                />
                <ListItemText
                  primary={input.name}
                  sx={{
                    '& .MuiListItemText-primary': styles.checkboxLabel,
                    textAlign: 'center',
                    opacity: input.selected ? 1 : 0.5
                  }}
                />
              </Paper>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
          <Button
            variant="contained"
            sx={{ ...styles.button, backgroundColor: theme.accent.success }}
            onClick={handleSave}
          >
            Save
          </Button>
          <Box sx={{ display: 'flex', flexDirection: 'column', }}>
            {props.inputs.length !== 12 && (
              <Typography component="body2" sx={{ color: theme.text.secondary, fontFamily: theme.fontFamily, textAlign: 'center' }}>Click reset to see more fields to SKU builder</Typography>
            )}
            <Button
              variant="contained"
              sx={{ ...styles.button, backgroundColor: theme.accent.secondary }}
              onClick={() => props.reset()}
            >
              Reset
            </Button>
          </Box>
          <Button
            variant="contained"
            sx={{ ...styles.button, backgroundColor: theme.accent.error }}
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SKUBuilderModal;